import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Papa from 'papaparse';
import { CalendarClock, ChevronRight, Clock, PiggyBank, Wallet } from 'lucide-react';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import { AccountsReportCsvDownloadButton } from '../components/AccountsReportCsvDownloadButton';
import { useAccountsReportToolbar } from '../contexts/AccountsReportToolbarContext';
import { getProjectExpendituresReports } from '../services/api/implementation/impl';
import { buildReturnToFromLocation, navigateWithReturn } from '../../shared/navigation';
import Loader from '../../shared/components/ui/Loader';
import {
  EmptyRow,
  Field,
  FilterBar,
  Table,
  TableCard,
  TBody,
  Td,
  Th,
  THead,
  Tile,
  TileRow,
  Tr
} from '../../shared/components/ui/primitives';
import { useProjectsList } from '../../../contexts/ProjectsContext';
import { money } from './SiteWallet/siteWalletUi';
import { filterInput } from './PerformaInvoiceList';

const KINDS = [
  { key: 'material', label: 'Material', hint: 'Vendor PIs' },
  {
    key: 'service_providers',
    label: 'Service providers',
    hint: 'Task bills, and running bills net of retention and TDS'
  },
  { key: 'labour', label: 'Labour wages (Operations)', hint: 'Wages and advances paid directly' },
  { key: 'site', label: 'Site expenses (wallet)', hint: 'Approved site wallet spends' }
];

const EMPTY = { project_id: '' };

const pct = (value) => (value == null ? '—' : `${value}%`);

const BudgetCell = ({ project }) => {
  if (!project.budget) return <span className="text-gray-400">No budget</span>;
  const over = project.budget_used_pct > 100;
  const near = project.budget_used_pct >= 80;
  return (
    <span className={over ? 'font-semibold text-danger-fg' : near ? 'text-warning-fg' : ''}>
      {pct(project.budget_used_pct)}
    </span>
  );
};

/**
 * What each project has cost: material, service providers and labour
 * wages and site expenses, paid and still pending, against the project's budget. Click a project
 * for its own page: month by month, by kind and block, and every bill behind it.
 */
const ProjectExpenditures = () => {
  const userId = Cookies.get('userId');
  const { setTabEndAction } = useAccountsReportToolbar();
  const { projects = [] } = useProjectsList();
  const [filters, setFilters] = useState(EMPTY);
  const navigate = useNavigate();
  const location = useLocation();
  const set = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const filtered = Object.values(filters).some(Boolean);

  const report = useQuery({
    queryKey: ['projectExpenditures', filters],
    queryFn: () => getProjectExpendituresReports(filters),
    keepPreviousData: true
  });
  const rows = report.data?.projects ?? [];
  const totals = report.data?.totals;
  const summary = report.data?.summary ?? {};

  useEffect(() => {
    mixpanel.track('Project Expenditures Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  const openProject = (id) =>
    navigateWithReturn(navigate, `/builder/reports/accounts/projects/${id}`, {
      returnTo: buildReturnToFromLocation(location)
    });

  // CSV of everything on screen: a line per project, then its blocks.
  const download = () => {
    if (!rows.length) {
      toast.error('Nothing to download for these filters.');
      return;
    }
    const lines = [];
    for (const p of rows) {
      lines.push({
        Project: p.name,
        Block: '',
        Material: p.material.paid,
        'Service providers': p.service_providers.paid,
        'Labour wages (Operations)': p.labour.paid,
        'Site expenses (wallet)': p.site.paid,
        'Total spent': p.total.paid,
        Pending: p.total.pending,
        Budget: p.budget ?? '',
        '% used': p.budget_used_pct ?? ''
      });
      for (const b of p.blocks) {
        lines.push({
          Project: p.name,
          Block: b.name,
          Material: b.total.paid,
          'Service providers': '',
          'Labour wages (Operations)': '',
          'Site expenses (wallet)': '',
          'Total spent': b.total.paid,
          Pending: b.total.pending,
          Budget: '',
          '% used': ''
        });
      }
    }
    const blob = new Blob([Papa.unparse(lines)], { type: 'text/csv;charset=utf-8;' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = 'project_expenditures.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(href);
  };
  const downloadRef = useRef(download);
  downloadRef.current = download;

  useEffect(() => {
    setTabEndAction(
      <AccountsReportCsvDownloadButton onClick={() => downloadRef.current()} disabled={false} />
    );
    return () => setTabEndAction(null);
  }, [setTabEndAction]);

  return (
    <div className="flex w-full flex-col gap-4 bg-gray-100 px-3 py-4">
      <Loader isVisible={report.isFetching && !report.data} />

      <TileRow>
        <Tile
          variant="card"
          label="Total spent"
          hint={filtered ? 'For these filters' : 'Paid so far'}
          value={money(summary.total_spent ?? 0)}
          icon={<Wallet size={16} />}
        />
        <Tile
          variant="card"
          label="Pending to pay"
          hint="Billed, not yet paid"
          value={money(summary.pending ?? 0)}
          icon={<Clock size={16} />}
          tone="warning"
        />
        <Tile
          variant="card"
          label="Budget used"
          hint={summary.budget ? `of ${money(summary.budget)}` : 'No project budgets set'}
          value={pct(summary.budget_used_pct)}
          icon={<PiggyBank size={16} />}
          tone={summary.budget_used_pct > 100 ? 'danger' : 'info'}
        />
        <Tile
          variant="card"
          label="Paid this month"
          hint="Since the 1st"
          value={money(summary.this_month ?? 0)}
          icon={<CalendarClock size={16} />}
          tone="success"
        />
      </TileRow>

      <FilterBar
        end={
          filtered && (
            <button
              type="button"
              onClick={() => setFilters(EMPTY)}
              className="mb-2 text-sm font-semibold text-brand-700 hover:underline"
            >
              Clear
            </button>
          )
        }
      >
        <Field label="Project" size="caption">
          <select value={filters.project_id} onChange={set('project_id')} className={filterInput}>
            <option value="">All projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
      </FilterBar>

      {report.isError ? (
        <p className="text-sm text-danger-fg">
          Couldn&apos;t load the report:{' '}
          {report.error?.response?.data?.message || report.error?.message}
        </p>
      ) : (
        <TableCard>
          <Table>
            <THead>
              <tr>
                <Th>Project</Th>
                {KINDS.map((k) => (
                  <Th key={k.key} align="right">
                    <span title={k.hint}>{k.label}</span>
                  </Th>
                ))}
                <Th align="right">Total spent</Th>
                <Th align="right">Pending</Th>
                <Th align="right">Budget</Th>
                <Th align="right">% used</Th>
              </tr>
            </THead>
            <TBody>
              {!rows.length && !report.isFetching && (
                <EmptyRow colSpan={9}>No projects{filtered ? ' for these filters' : ''}.</EmptyRow>
              )}
              {rows.map((p) => (
                <Tr
                  key={p.project_id}
                  // Labour paid with no project has no page of its own.
                  onClick={p.project_id ? () => openProject(p.project_id) : undefined}
                >
                  <Td
                    className={p.project_id ? 'font-medium text-brand-700' : 'italic text-gray-600'}
                  >
                    {p.project_id ? (
                      <span className="inline-flex items-center gap-1">
                        {p.name}
                        <ChevronRight size={14} className="text-gray-400" />
                      </span>
                    ) : (
                      <span title="Labour payments recorded without a project">{p.name}</span>
                    )}
                  </Td>
                  {KINDS.map((k) => (
                    <Td key={k.key} align="right" numeric className="text-gray-700">
                      {money(p[k.key].paid)}
                    </Td>
                  ))}
                  <Td align="right" numeric className="font-semibold">
                    {money(p.total.paid)}
                  </Td>
                  <Td
                    align="right"
                    numeric
                    className={p.total.pending > 0 ? 'text-warning-fg' : ''}
                  >
                    {money(p.total.pending)}
                  </Td>
                  <Td align="right" numeric className="text-gray-600">
                    {p.budget ? money(p.budget) : '—'}
                  </Td>
                  <Td align="right">
                    <BudgetCell project={p} />
                  </Td>
                </Tr>
              ))}
              {rows.length > 1 && totals && (
                <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
                  <Td>Total</Td>
                  {KINDS.map((k) => (
                    <Td key={k.key} align="right" numeric>
                      {money(totals[k.key].paid)}
                    </Td>
                  ))}
                  <Td align="right" numeric>
                    {money(totals.total.paid)}
                  </Td>
                  <Td align="right" numeric>
                    {money(totals.total.pending)}
                  </Td>
                  <Td align="right" numeric>
                    {summary.budget ? money(summary.budget) : '—'}
                  </Td>
                  <Td align="right">{pct(summary.budget_used_pct)}</Td>
                </tr>
              )}
            </TBody>
          </Table>
        </TableCard>
      )}
      <p className="text-xs text-gray-500">
        Amounts in the table are paid. The date filter picks bills by when they were raised, wages
        by when they were paid and site expenses by when they were spent.
      </p>
    </div>
  );
};

export default ProjectExpenditures;
